// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton, Badge, Box, Typography, Paper, Divider, Button
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import api from '../utils/api';
import { GLASS, GLASS_BORDER } from '../theme';

// Une icône par type émis par le backend (`Notification(` dans backend/app).
// `subscribe_request` est le canal par lequel un prospect établissement arrive.
// Un type absent de la table, comme `slot_request` encore présent en base,
// s'affiche avec l'icône générique et sans action.
const ICONS = {
  subscribe_request: <PersonAddIcon fontSize="small" sx={{ color: 'primary.main' }} />,
  payment_confirmed: <PaymentIcon fontSize="small" sx={{ color: 'success.main' }} />,
  payment_recovered: <PaymentIcon fontSize="small" sx={{ color: 'success.main' }} />,
  payment_failed: <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />,
  dispute_opened: <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />,
  subscription_cancelled: <ErrorOutlineIcon fontSize="small" sx={{ color: 'warning.main' }} />,
  subscription_updated: <PaymentIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  camera_order: <VideocamIcon fontSize="small" sx={{ color: 'primary.main' }} />,
};

// Le panneau se superpose au tableau en cours de lecture : il est opaque
// (`background.paper`), et non en verre comme les cartes, pour que les lignes
// du dessous ne brouillent pas les notifications. Même règle que la console de
// gestion, qui proscrit le flou sur toute surface superposée ou répétée. Une
// notification non lue prend le fond d'un élément sélectionné.
const UNREAD_HOVER = 'rgba(59,130,246,.22)';
const READ_HOVER = 'rgba(126,179,255,.08)';

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchCount = () => {
    api.get('/notifications/unread-count')
      .then(r => r.data && setCount(r.data.count))
      .catch(() => {});
  };

  // Un refus du serveur vide la liste ; une panne réseau laisse la précédente.
  const fetchNotifications = () => {
    api.get('/notifications/')
      .then(r => setNotifications(r.data))
      .catch((e) => { if (e.response) setNotifications([]); });
  };

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (!open) {
      fetchNotifications();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
    } catch (e) {
      // Une panne réseau laisse le panneau tel quel. Un refus du serveur ne
      // l'empêche pas de tout afficher lu : le relevé suivant du compteur
      // rétablit le nombre réel.
      if (!e.response) return;
    }
    setCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      api.post(`/notifications/${notif.id}/read`).catch(() => {});
      setCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    }
    const clientId = notif.data && notif.data.client_id;
    if (clientId) {
      setOpen(false);
      navigate(`/clients/${clientId}`);
    }
  };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso)) / 1000;
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return `Il y a ${Math.floor(diff / 86400)}j`;
  };

  return (
    <Box ref={ref} sx={{ position: 'relative' }}>
      <IconButton onClick={handleOpen} sx={{ color: open ? 'text.primary' : 'text.secondary' }}>
        <Badge badgeContent={count} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      {open && (
        <Paper
          sx={{
            position: 'absolute', right: 0, top: '110%', width: 360,
            borderRadius: '16px', overflow: 'hidden', zIndex: 1300,
            bgcolor: 'background.paper', border: `1px solid ${GLASS_BORDER}`,
            backdropFilter: 'none', boxShadow: '0 14px 36px rgba(4,9,20,.6)',
          }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: GLASS }}>
            <Typography variant="subtitle2" fontWeight="bold">Notifications</Typography>
            {count > 0 && (
              <Button size="small" onClick={markAllRead} sx={{ fontSize: 11 }}>
                Tout marquer lu
              </Button>
            )}
          </Box>
          <Divider />

          {notifications.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">Aucune notification</Typography>
            </Box>
          )}

          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifications.map((notif, i) => (
              <React.Fragment key={notif.id}>
                {i > 0 && <Divider />}
                <Box
                  onClick={() => handleNotifClick(notif)}
                  sx={{
                    px: 2, py: 1.5, cursor: 'pointer',
                    bgcolor: notif.is_read ? 'transparent' : 'action.selected',
                    '&:hover': { background: notif.is_read ? READ_HOVER : UNREAD_HOVER },
                    display: 'flex', gap: 1.5, alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ mt: 0.3 }}>{ICONS[notif.type] || <NotificationsIcon fontSize="small" />}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{notif.message}</Typography>
                    <Typography variant="caption" color="text.secondary">{timeAgo(notif.created_at)}</Typography>
                  </Box>
                  {!notif.is_read && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.light', mt: 0.8, flexShrink: 0 }} />
                  )}
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
